import { BaseComponent } from "../BaseComponent";

import { Guid } from "../../Utils/Guid";

import { LightboxStore } from "../../Flux/Variables";

import { Portal } from "./Portal";

import * as React from "react";
import { CSSTransition } from "react-transition-group";

import { LightboxActionsCreator } from "../../Flux/Lightboxes/LightboxActionsCreator";
import { Div } from "../Div";
import { SVGicon, svgIcons } from "../SVGicon";
import * as classNames from "classNames";
import { Content } from "../Resources/Content";
import * as $ from "jquery";

export interface ILightboxProps extends React.Props<Lightbox> {
    title: string;
    contentAfterTitle?: JSX.Element;
    contentFooter?: JSX.Element;

    show: boolean;
    onClose: () => void;

    type?: LightboxType;
    cssWrapper?: string;
    contentCssCustom?: string;

    unclosable?: boolean;
    noOverlay?: boolean;

    // Use this only when you're focusing some part of the content of the lightbox manually!
    // (e.g.the pre - onboarding FAQ side panel)
    noAutofocus?: boolean;
    contentId?: string;

    isClickOutsideClose?: boolean;
}

export interface ILightboxState {
    isCurrentLightbox: boolean;
    openedLightboxes: string[];
    scrollbarWidth?: number;
}

export const enum LightboxType {
    Modal,
    FAQ,
    OffCanvasMenu,
    Contact,
}

export class Lightbox extends BaseComponent<ILightboxProps, ILightboxState> {
    private _animationTimeout = 300;

    private _siteContainerSelector = ".site-container, .site-footer, .layout-secured";
    private _lightboxPortalSelector = "[data-lightbox-target]";

    private _hasLightboxOpenCssClass = "has-lightbox-open";
    private _hasLightboxOffcanvasOpenCssClass = "has-lightbox-offcanvas-menu-open";
    private _hasLightboxOffcanvasClosedCssClass = "has-lightbox-offcanvas-menu-closed";

    private _lightboxContainerCssClass = "lightbox-container";
    private _lightboxWrapperCssClass = "lightbox-wrapper";
    private _lightboxWrapperOffCanvasMenuCssClass = "lightbox-wrapper-offcanvas-menu";
    private _lightboxWrapperContactCssClass = "lightbox-wrapper-contact";

    private _lightboxId = Guid.getNext();
    private _titleId = Guid.getNext();
    private _modalRef: HTMLDivElement;
    private _exitBtnRef: HTMLButtonElement;
    private _previousFocus: any;
    private _scrollTop = 0;
    private _isMounted = false;

    public static defaultProps = {
        isClickOutsideClose: true,
    };

    constructor(props: ILightboxProps) {
        super(props);

        this.state = {
            isCurrentLightbox: false,
            openedLightboxes: LightboxStore.getPayload() ?? [],
            scrollbarWidth: 0,
        };

        this.subscribeToStore(LightboxStore, () => this.onLightboxStoreChanged());
    }

    public componentDidMount(): void {
        this._isMounted = true;
        if (this.props.show) {
            this.doOpenLightbox();
        }
    }

    public componentWillUpdate(nextProps: ILightboxProps): void {
        if (!this.props.show && nextProps.show) {
            this.doOpenLightbox();
        }

        if (this.props.show && !nextProps.show) {
            this.doCloseLightbox();
        }
    }

    public componentWillUnmount(): void {
        this._isMounted = false;
        this.doCloseLightbox();
    }

    public render(): JSX.Element {
        return (
            <Portal container={document.querySelector(this._lightboxPortalSelector) as HTMLElement}>
                <CSSTransition
                    unmountOnExit
                    in={this._isMounted}
                    classNames="a-lightbox"
                    timeout={this._animationTimeout}
                >
                    <div className={ classNames(this._lightboxWrapperCssClass, this.props.cssWrapper) }> {this.getLightBoxContent()}</div>
                </CSSTransition>
            </Portal>
        );
    }

    private getLightBoxContent(): JSX.Element {
        let containerClass = this._lightboxContainerCssClass;
        containerClass +=
            this.props.type === LightboxType.OffCanvasMenu ? " " + this._lightboxWrapperOffCanvasMenuCssClass : "";
        containerClass += this.props.type === LightboxType.Contact ? " " + this._lightboxWrapperContactCssClass : "";

        return (
            <div className={containerClass}>
                {!this.props.noOverlay && (
                    <div className="lightbox-overlay" onClick={(e: any) => this.closeLightbox(e)} />
                )}
                {this.getLightboxWindow()}
            </div>
        );
    }

    private getLightboxWindow(): JSX.Element {
        if (typeof this.props.type === "undefined" || this.props.type === LightboxType.Modal) {
            return (
                <div
                    className={classNames(
                        "lightbox-modal",
                        { "show-scrollbar": !!this.state.scrollbarWidth }
                    )}
                    id={this._lightboxId}
                    role="dialog"
                    tabIndex={-1}
                    aria-labelledby={this._titleId}
                    aria-hidden={!this.state.isCurrentLightbox}
                    data-inert={!this.state.isCurrentLightbox}
                    ref={(divElement: HTMLDivElement) => (this._modalRef = divElement)}
                >
                    <Div className="inner" onClickOutside={(e: any) => this.onClickOutside(e)}>
                        <div className="header">
                            {this.getCloseButton()}
                            <h1 id={this._titleId}>{this.props.title}</h1>
                            {this.props.contentAfterTitle}
                        </div>
                        <div className={classNames(this.props.contentCssCustom, 'content' )} id={this.props.contentId}>
                            {this.props.children}
                        </div>
                    </Div>
                </div>
            );
        }

        if (this.props.type === LightboxType.FAQ) {
            return (
                <div
                    className={classNames(
                        "lightbox-faq",
                        { "show-scrollbar": !!this.state.scrollbarWidth }
                    )}
                    id={this._lightboxId}
                    role="region"
                    tabIndex={-1}
                    aria-labelledby={this._titleId}
                    aria-hidden={!this.state.isCurrentLightbox}
                    data-inert={!this.state.isCurrentLightbox}
                    ref={(divElement: HTMLDivElement) => (this._modalRef = divElement)}
                >
                    <Div className="inner" onClickOutside={(e: any) => this.onClickOutside(e)}>
                        <div className="header">
                            {this.getCloseButton()}
                            <h1 id={this._titleId}>{this.props.title}</h1>
                        </div>
                        <div className="content" id={this.props.contentId}>
                            {this.props.children}
                        </div>
                    </Div>
                </div>
            );
        }

        if (this.props.type === LightboxType.OffCanvasMenu) {
            return (
                <div
                    className={classNames(
                        "lightbox-offcanvas-menu",
                        { "show-scrollbar": !!this.state.scrollbarWidth }
                    )}
                    id={this._lightboxId}
                    role="region"
                    tabIndex={-1}
                    aria-labelledby={this._titleId}
                    aria-hidden={!this.state.isCurrentLightbox}
                    data-inert={!this.state.isCurrentLightbox}
                    ref={(divElement: HTMLDivElement) => (this._modalRef = divElement)}
                >
                    <Div className="inner" onClickOutside={(e: any) => this.onClickOutside(e)}>
                        <h1 hidden={true} id={this._titleId}>
                            {this.props.title}
                        </h1>
                        {this.getCloseButton()}
                        {this.props.children}
                    </Div>
                </div>
            );
        }

        if (this.props.type === LightboxType.Contact) {
            return (
                <div
                    className={classNames(
                        "lightbox-contact",
                        { "show-scrollbar": !!this.state.scrollbarWidth }
                    )}
                    id={this._lightboxId}
                    role="dialog"
                    tabIndex={-1}
                    aria-labelledby={this._titleId}
                    aria-hidden={!this.state.isCurrentLightbox}
                    data-inert={!this.state.isCurrentLightbox}
                    ref={(divElement: HTMLDivElement) => (this._modalRef = divElement)}
                >
                    <Div className="inner" onClickOutside={(e: any) => this.onClickOutside(e)}>
                        <div className="header">
                            {this.getCloseButton()}
                            <h1 id={this._titleId}>{this.props.title}</h1>
                        </div>
                        <div className={classNames(this.props.contentCssCustom, 'content' )}>
                            {this.props.children}
                        </div>
                        <div className="footer">{this.props.contentFooter}</div>
                    </Div>
                </div>
            );
        }
    }

    private getCloseButton(): JSX.Element {
        if (this.props.unclosable) {
            return null;
        }

        return (
            <button
                type="button"
                className="close-btn"
                title="Close"
                onClick={(e: any) => this.closeLightbox(e)}
                ref={(exitBtnRef: HTMLButtonElement) => (this._exitBtnRef = exitBtnRef)}
            >
                <span className="sr-only">{Content.Close}</span>
                <span className="icon" aria-hidden="true"></span>
            </button>
        );
    }

    private onLightboxStoreChanged(): void {
        if (this._isMounted) {
            this.updateStatePart((x: ILightboxState) => (x.openedLightboxes = LightboxStore.getPayload()));
            this.checkIfIsCurrentLightbox();
        }
    }

    private doOpenLightbox(): void {
        this._scrollTop = $(document).scrollTop();
        this.addOpenClassToBody();
        this.bindEscKey();
        this.checkIfIsCurrentLightbox();
        setTimeout(() => this.focusLightbox(), this._animationTimeout);

        LightboxActionsCreator.update(null);
        LightboxActionsCreator.update(
            this.state.openedLightboxes.filter((l) => l !== this._lightboxId).concat([this._lightboxId])
        );
    }

    private doCloseLightbox(): void {
        this.removeEventListeners();

        LightboxActionsCreator.update(null);
        LightboxActionsCreator.update(this.state.openedLightboxes.filter((l) => l !== this._lightboxId));

        if (this.state.openedLightboxes.filter((l) => l !== this._lightboxId).length === 0) {
            setTimeout(() => this.removeOpenClassFromBody(), this._animationTimeout);
            this.removeOffCanvasMenuOpenClassFromBody();
        }

        if (this._previousFocus) {
            setTimeout(() => this._previousFocus.focus(), this._animationTimeout);
        }
    }

    private checkIfIsCurrentLightbox(): void {
        if (this.state.openedLightboxes[this.state.openedLightboxes.length - 1] === this._lightboxId) {
            if (!this.state.isCurrentLightbox) {
                this.updateStatePart((x: ILightboxState) => (x.isCurrentLightbox = true));
            }
        } else {
            if (this.state.isCurrentLightbox) {
                this.updateStatePart((x: ILightboxState) => (x.isCurrentLightbox = false));
            }
        }
    }

    private escKeyListener(event: KeyboardEvent): void {
        if (this.props.show && event.keyCode === 27 && !this.props.unclosable) {
            this.closeLightbox(event);
        }
    }

    private bindEscKey(): void {
        if (!this.props.unclosable) {
            setTimeout(
                () =>
                    this._modalRef && this._modalRef.addEventListener("keydown", this.escKeyListener.bind(this), false),
                this._animationTimeout
            );
        }
    }

    private onClickOutside(event: any): void {
        if (this.props.isClickOutsideClose) {
            this.closeLightbox(event);
        }
    }

    private closeLightbox(event: any): void {
        event.preventDefault();

        if (!this.props.unclosable && this.state.isCurrentLightbox) {
            this.props.onClose();
        }
    }

    private focusLightbox(): void {
        this._previousFocus = document.activeElement;
        if (!this.props.noAutofocus && this._modalRef) {
            this._modalRef.focus();
        }
    }

    private removeEventListeners(): void {
        if (this._modalRef) {
            this._modalRef.removeEventListener("keydown", this.escKeyListener, false);
        }
    }

    private addOpenClassToBody(): void {
        const scrollbarWidth = Math.max(0, window.innerWidth - document.body.clientWidth);
        this.updateStatePart((x: ILightboxState) => (x.scrollbarWidth = scrollbarWidth));

        const body = $("body");
        if (!body.hasClass(this._hasLightboxOpenCssClass)) {
            body.addClass(this._hasLightboxOpenCssClass);
            document.body.style.setProperty("top", -this._scrollTop + "px");
            document.body.style.setProperty("width", "calc(100vw - " + scrollbarWidth + "px)");
            document.body.style.setProperty("--scrollbar-width", scrollbarWidth + "px");
            this.addOffCanvasMenuOpenClassToBody();
        }
    }

    private removeOpenClassFromBody(): void {
        const body = $("body");
        if (body.hasClass(this._hasLightboxOpenCssClass)) {
            body.removeClass(this._hasLightboxOpenCssClass);
            document.body.style.removeProperty("top");
            document.body.style.removeProperty("width");
            document.body.style.removeProperty("--scrollbar-width");
            $("html, body").scrollTop(this._scrollTop);
            $(this._siteContainerSelector).attr("aria-hidden", "false").attr("data-inert", "false");
        }
    }

    private addOffCanvasMenuOpenClassToBody(): void {
        const body = $("body");
        body.removeClass(this._hasLightboxOffcanvasClosedCssClass);
        if (this.props.type === LightboxType.OffCanvasMenu) {
            body.addClass(this._hasLightboxOffcanvasOpenCssClass);
        }
    }

    private removeOffCanvasMenuOpenClassFromBody(): void {
        const body = $("body");
        if (this.props.type === LightboxType.OffCanvasMenu) {
            body.removeClass(this._hasLightboxOffcanvasOpenCssClass);
            body.addClass(this._hasLightboxOffcanvasClosedCssClass);
        }
    }
}
